import { z } from 'zod'

import type { Voice } from '@/types/assistant'
import type { VoiceCreateInput, VoiceMetaInput } from '@/types/voiceAdmin'

// Backend limits (VoiceCreate/VoiceUpdate): oversized values 422 server-side,
// mirrored here so the admin sees the field, not a generic toast.
const NAME_MAX_LENGTH = 255
const DESCRIPTION_MAX_LENGTH = 2000

const REQUIRED = 'resources.admin.voices.errors.required'
const PRIORITY_RANGE = 'resources.admin.voices.errors.priority_range'

export const voiceSchema = z
  .object({
    display_name: z
      .string()
      .min(1, REQUIRED)
      .max(NAME_MAX_LENGTH, 'resources.admin.voices.errors.display_name_too_long'),
    voice_id: z
      .string()
      .min(1, REQUIRED)
      .max(NAME_MAX_LENGTH, 'resources.admin.voices.errors.voice_id_too_long'),
    provider_id: z.string().min(1, REQUIRED),
    description: z
      .string()
      .trim()
      .min(1, REQUIRED)
      .max(DESCRIPTION_MAX_LENGTH, 'resources.admin.voices.errors.description_too_long'),
    is_public: z.boolean(),
    // meta (flattened)
    naturalness: z
      .string()
      .refine(
        (v) => v.trim() === '' || (Number.isInteger(Number(v)) && Number(v) >= 1 && Number(v) <= 5),
        'resources.admin.voices.errors.score_range'
      ),
    speed: z
      .string()
      .refine(
        (v) => v.trim() === '' || (Number.isInteger(Number(v)) && Number(v) >= 1 && Number(v) <= 5),
        'resources.admin.voices.errors.score_range'
      ),
    suggested: z.boolean(),
    suggested_weight: z.coerce
      .number({ message: PRIORITY_RANGE })
      .int(PRIORITY_RANGE)
      .min(0, PRIORITY_RANGE),
    languages: z.array(z.string()).min(1, 'resources.admin.voices.errors.languages_required'),
    native_languages: z.array(z.string()),
    default_for: z.array(z.string()),
    gender: z.string().optional().nullable(),
    age_group: z.string().optional().nullable(),
    tones: z.array(z.string()),
    pricing_tier: z.enum(['included', 'premium']),
    // Provider config (only `rate` and `model_id` are editable; other keys are preserved on save).
    // `rate_default` is the "provider default" switch; `rate` is only read when it is off.
    rate_default: z.boolean(),
    rate: z
      .string()
      .refine(
        (v) =>
          v.trim() === '' || (Number.isFinite(Number(v)) && Number(v) >= 0.5 && Number(v) <= 2),
        'resources.admin.voices.errors.rate_range'
      ),
    // Empty string is the "default" option: the key is omitted and the backend picks the model.
    model_id: z.string(),
  })
  .superRefine((values, ctx) => {
    // Scores are serialized as a pair; a lone value would silently be dropped,
    // so require both or neither.
    const nat = values.naturalness.trim()
    const spd = values.speed.trim()
    if ((nat === '') !== (spd === '')) {
      ctx.addIssue({
        code: 'custom',
        path: [nat === '' ? 'naturalness' : 'speed'],
        message: 'resources.admin.voices.errors.scores_pair',
      })
    }
    // A cleared rate with the provider-default switch off would silently save
    // as "provider default"; make the admin pick one.
    if (!values.rate_default && values.rate.trim() === '') {
      ctx.addIssue({
        code: 'custom',
        path: ['rate'],
        message: 'resources.admin.voices.errors.rate_required',
      })
    }
  })

export type VoiceFormValues = z.infer<typeof voiceSchema>

export const DEFAULT_VALUES: VoiceFormValues = {
  display_name: '',
  voice_id: '',
  provider_id: '',
  description: '',
  is_public: true,
  naturalness: '',
  speed: '',
  suggested: false,
  suggested_weight: 0,
  languages: [],
  native_languages: [],
  default_for: [],
  gender: null,
  age_group: null,
  tones: [],
  pricing_tier: 'included',
  rate_default: true,
  rate: '',
  model_id: '',
}

/** Flatten a Voice + its nested meta into the flat form value shape. */
export function voiceToFormValues(voice: Voice): VoiceFormValues {
  const meta = voice.meta ?? {}
  const rate = voice.config?.rate
  const modelId = voice.config?.model_id
  return {
    display_name: voice.display_name ?? '',
    voice_id: voice.voice_id ?? '',
    provider_id: voice.provider?.id != null ? String(voice.provider.id) : '',
    description: voice.description ?? '',
    is_public: voice.is_public ?? true,
    naturalness: meta.scores?.naturalness != null ? String(meta.scores.naturalness) : '',
    speed: meta.scores?.speed != null ? String(meta.scores.speed) : '',
    suggested: meta.suggested ?? false,
    suggested_weight: meta.suggested_weight ?? 0,
    languages: meta.languages ?? [],
    native_languages: meta.native_languages ?? [],
    default_for: meta.default_for ?? [],
    gender: meta.gender ?? null,
    age_group: meta.age_group ?? null,
    tones: meta.tones ?? [],
    // Voices predating the selector default to 'included' when opened.
    pricing_tier: meta.pricing?.tier === 'premium' ? 'premium' : 'included',
    rate_default: rate == null,
    rate: rate != null ? String(rate) : '',
    model_id: typeof modelId === 'string' ? modelId : '',
  }
}

/**
 * Re-nest flat form values into the create/update input (with nested meta).
 * `baseConfig`/`baseMeta` are the voice's current server state: only the keys
 * the UI exposes are overwritten, so other provider settings (stability,
 * style, …) and future meta keys survive the whole-object replacement the
 * backend applies. Scores are omitted when empty; pricing is always
 * normalized to a tier.
 */
export function formValuesToInput(
  values: VoiceFormValues,
  baseConfig: Record<string, unknown> = {},
  baseMeta: VoiceMetaInput = {}
): VoiceCreateInput {
  const config: Record<string, unknown> = { ...baseConfig }
  if (values.rate_default) {
    delete config.rate
  } else {
    config.rate = Number(values.rate)
  }
  // The backend rejects an explicit null: omit the key to select the default model.
  if (values.model_id === '') {
    delete config.model_id
  } else {
    config.model_id = values.model_id
  }

  const nat = values.naturalness.trim()
  const spd = values.speed.trim()

  const meta: VoiceMetaInput = {
    ...baseMeta,
    suggested: values.suggested,
    suggested_weight: values.suggested_weight,
    languages: values.languages,
    native_languages: values.native_languages,
    default_for: values.default_for,
    tones: values.tones,
    gender: values.gender || undefined,
    age_group: values.age_group || undefined,
    pricing: { tier: values.pricing_tier },
    scores: nat !== '' && spd !== '' ? { naturalness: Number(nat), speed: Number(spd) } : undefined,
  }

  return {
    config,
    meta,
    display_name: values.display_name.trim(),
    voice_id: values.voice_id.trim(),
    provider_id: Number(values.provider_id),
    description: values.description.trim(),
    is_public: values.is_public,
  }
}
